import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IImageWrapper } from '../../../models/image-wrapper';
import { FormControl, FormGroup } from '@angular/forms';
import { CcImageSearchResult, CcImageSearchService } from '../services/cc-image-search.service';
import { Observable, Subject } from 'rxjs';
import { ExternalImageSearchApis } from '../../../config/external-image-search-api.config';
import { MainConfig } from '../../../config/main.config';
import { ToastService } from '../../services/toast.service';
import { Licenses, LicensesHumanReadable } from '../../../models/licenses';
import { IStandardizedImageSearchResults } from '../../../models/standardized-image-search-results';
import { FlickrImageSearchService, IFlickrImageSearchResult } from '../services/flickr-image-search.service';
import { SessionServiceService } from '../../services/session-service.service';
import { BackendService } from '../../services/backend.service';
import { IResponseRecognizeImage } from '@oer-converter/api-types';
import { Router } from '@angular/router';
import { DialogShowSelectedImageFromFileComponent } from '../dialog-show-selected-image-from-file/dialog-show-selected-image-from-file.component';
import { MatDialog } from '@angular/material/dialog';
import { TensorflowClassifyImageService } from '../services/tensorflow-classify-image.service';
import { DuckduckgoImageSearchService, IDuckduckgoImageSearchResult } from '../services/duckduckgo-image-search.service';
import { GoogleImageSearchService, IGoogleImageSearchResult } from '../services/google-image-search.service';
import { LoggerService } from '../../services/logger/logger.service';
import { UI_ACTION_CONSTANTS } from '../../services/logger/constants';

/**
 * This component will provide means to look for suitable replacements for the passed image inside the image wrapper online
 * @input image image wrapper which contains the image that is to be toReplace
 */
@Component({
  selector: 'app-search-image-online',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, AfterViewInit {
  @Output('select') _selectedImage: EventEmitter<IImageWrapper> = new EventEmitter<IImageWrapper>();
  @Input('clear-selection') clearSelection$: Observable<string> = new Observable<string>();

  public searchStandardizedResultsSubject: Subject<IStandardizedImageSearchResults[]> = new Subject<IStandardizedImageSearchResults[]>();

  public searchFormGroup!: FormGroup;

  public externalImageSearchApis: { key: string; name: string }[] = new Array<{ key: string; name: string }>();

  public showPagination: boolean = false;
  public currentPage: number = 1;
  public hasNextPage: boolean = false;

  public licenses: number[] = [];
  public licensesHumanReadable = LicensesHumanReadable;

  public showingResults: boolean = false;

  public querySuggestions: string[] = [];

  public failedTargetedApis: string[] = [];

  public autoClassifyImages: boolean = false;

  public loadingResults: boolean = false;

  private _lastSearchKey: string = '';

  constructor(
    private _toastie: ToastService,
    private _ccImgSearch: CcImageSearchService,
    private _flickrImgSearch: FlickrImageSearchService,
    public session: SessionServiceService,
    private _logger: LoggerService,
    private _backend: BackendService,
    private _router: Router,
    private _dialogSelectedImageFromFile: MatDialog,
    private _session: SessionServiceService,
    private tensorFlowClassifyImg: TensorflowClassifyImageService,
    private duckduckgoSearch: DuckduckgoImageSearchService,
    private googleSearch: GoogleImageSearchService
  ) { }

  /**
   * Init the form and its options
   */
  ngOnInit() {
    const _defaultTargetApiConfigKey = MainConfig.defaultTargetApi;
    const _defaultTargetApiConfigObject = ExternalImageSearchApis[_defaultTargetApiConfigKey];

    const _availableApis: string[] = [];
    for (const key in ExternalImageSearchApis) {
      if (ExternalImageSearchApis.hasOwnProperty(key)) {
        _availableApis.push(key);
      }
    }

    for (const license in Licenses) {
      const index = Number(license);
      if (!isNaN(index) && Licenses[index].localeCompare('UNKNOWN') !== 0 && index <= this._session.getTargetLicense()) {
        this.licenses.push(index);
      }
    }

    this.searchFormGroup = new FormGroup({
      query: new FormControl(),
      targetApi: new FormControl(_availableApis),
      license: new FormControl(this.licenses)
    });

    for (const key in ExternalImageSearchApis) {
      if (ExternalImageSearchApis.hasOwnProperty(key)) {
        this.externalImageSearchApis.push({ name: ExternalImageSearchApis[key].name, key: key });
      }
    }

    if (this._session.getAutoClassifyImages()) {
      this.autoClassifyImages = true;
      this.getClientAutoImageResults(true);
    }
  }

  ngAfterViewInit(): void {}

  /**
   * Submits the query to the chosen targets
   */
  public async submitQuery(): Promise<void> {
    const targetedApis: string[] = this.searchFormGroup.get('targetApi')?.value ?? [];
    const targetedLicenses: Licenses[] = this.searchFormGroup.get('license')?.value ?? [];
    const queryRaw: any = this.searchFormGroup.get('query')?.value;
    const query: string = (typeof queryRaw === 'string' ? queryRaw : '').trim();

    const searchKey = JSON.stringify({
      query,
      targetedApis: [...targetedApis].sort(),
      targetedLicenses: [...targetedLicenses].sort()
    });

    if (this._lastSearchKey !== '' && this._lastSearchKey !== searchKey) {
      this.currentPage = 1;
    }
    this._lastSearchKey = searchKey;

    const currentPage: number = this.currentPage;

    if (!query) {
      this._session.alert('Missing search query.', 'Please enter a search term.');
      return;
    }

    const results: IStandardizedImageSearchResults[] = [];
    const apiPromises: Promise<void>[] = [];
    let anyShowPagination = false;
    let anyHasNextPage = false;

    this.failedTargetedApis = [];
    this.loadingResults = true;

    const selected = await Promise.resolve(this.session.getCurrentlySelectedImageFromFile?.() ?? []);
    const sourceImage = Array.isArray(selected) ? selected.map((img: any) => img?.source).filter(Boolean) : [];

    this._logger.log(UI_ACTION_CONSTANTS.EXTERNAL_IMAGE_SEARCHED, {
      sourceImage,
      isAutoClassifyImages: this.autoClassifyImages,
      isAutoSearchQueryClicked: this.querySuggestions.length > 0,
      isAutoSearchQueryOptionSelected: this.querySuggestions.includes(query),
      searchQuery: query,
      targetedApis,
      targetedLicenses: targetedLicenses.map(l => this.licensesHumanReadable[l]),
      currentPage
    });

    this.session.awaitAsyncAction('primary', $localize`Searching for images...`);

    const run = (apiKey: string, p: Promise<void>) => {
      apiPromises.push(
        p.catch(() => {
          this.failedTargetedApis.push(apiKey);
          this._session.alert('Loading image failed.', 'Please try again later or choose a different source api.');
        })
      );
    };

    // Openverse (cc)
    if (targetedApis.includes('cc')) {
      run(
        'cc',
        this._ccImgSearch.query(query, { license: targetedLicenses, page: currentPage })
          .then((raw: CcImageSearchResult) => {
            if (raw?.page_count > 1) {
              anyShowPagination = true;
              if (raw.page_count > currentPage) {
                anyHasNextPage = true;
              }
            }

            results.push(
              this._ccImgSearch.convertToStandardizedApiResults(raw, currentPage, targetedLicenses)
            );
          })
      );
    }

    // Flickr
    if (targetedApis.includes('flickr')) {
      run(
        'flickr',
        (async () => {
          let effectivePage = currentPage;

          let raw: IFlickrImageSearchResult = await this._flickrImgSearch.query(query, {
            license: targetedLicenses,
            page: effectivePage
          });

          const initialCount = raw?.photos?.photo?.length ?? 0;

          // If user is on a later page that comes back empty for Flickr, retry page 1 once
          if (effectivePage > 1 && initialCount === 0) {
            effectivePage = 1;
            this.currentPage = 1;

            raw = await this._flickrImgSearch.query(query, {
              license: targetedLicenses,
              page: 1
            });
          }

          const pages = Number(raw?.photos?.pages ?? 0);
          if (pages > 1) {
            anyShowPagination = true;
            if (pages > effectivePage) {
              anyHasNextPage = true;
            }
          }

          const standardized = await this._flickrImgSearch.convertToStandardizedApiResults(
            raw,
            effectivePage,
            targetedLicenses
          );

          if ((standardized?.results?.length ?? 0) > 0) {
            results.push(standardized);
          }
        })()
      );
    }

    // DuckDuckGo
    if (targetedApis.includes('duckduckgo')) {
      run(
        'duckduckgo',
        this.duckduckgoSearch.query(query, { license: targetedLicenses[0], page: currentPage })
          .then(async (raw: IDuckduckgoImageSearchResult) => {
            const standardized = await this.duckduckgoSearch.convertToStandardizedApiResults(
              raw,
              currentPage,
              targetedLicenses
            );
            if ((standardized?.results?.length ?? 0) > 0) {
              results.push(standardized);
            }
          })
      );
    }

    // Google
    if (targetedApis.includes('google')) {
      run(
        'google',
        this.googleSearch.query(query, { license: targetedLicenses[0], page: currentPage })
          .then(async (raw: IGoogleImageSearchResult) => {
            const standardized = await this.googleSearch.convertToStandardizedApiResults(
              raw,
              currentPage,
              targetedLicenses
            );
            if ((standardized?.results?.length ?? 0) > 0) {
              results.push(standardized);
            }
          })
      );
    }

    await Promise.all(apiPromises);

    this.showPagination = anyShowPagination;
    this.hasNextPage = anyHasNextPage;

    this._logger.log(
      UI_ACTION_CONSTANTS.EXTERNAL_IMAGE_FETCHED,
      { sourceImage },
      {
        success: this.failedTargetedApis.length === 0,
        failedTargetedApis: this.failedTargetedApis
      }
    );

    if (results.length > 0) {
      this.showingResults = true;
      this.searchStandardizedResultsSubject.next(results);
    } else {
      this.showingResults = false;
    }

    this.loadingResults = false;
    this.session.doneAsyncAction();
  }

  /**
   * Cascade up the selected image up to the main component
   * @param image The image that was selected from the results
   */
  public onSelectedImage(image: IImageWrapper): void {
    this._selectedImage.emit(image);
  }

  /**
   * Calls the API to get predictions what might be on the given image and uses the first prediction
   */
  public async getAutoImageResults(): Promise<void> {
    const selectedImageFromFile: IImageWrapper[] = await this.session.getCurrentlySelectedImageFromFile();
    this.session.awaitAsyncAction();
    const imagePrediction: IResponseRecognizeImage = await this._backend.getImageClassifications(selectedImageFromFile[0].base64);
    this.searchFormGroup.get('query')?.setValue(imagePrediction[0].className);
    this.submitQuery();
    this.session.doneAsyncAction();
  }

  public async getClientAutoImageResults(submitQuery: boolean = false) {
    this.session.awaitAsyncAction('primary', $localize`Classifying image...`);
    const selectedImageFromFile: IImageWrapper[] = this.session.getCurrentlySelectedImageFromFile();
    const res = await this.tensorFlowClassifyImg.classifyImage(selectedImageFromFile[0].base64);

    this._logger.log(
      UI_ACTION_CONSTANTS.EXTERNAL_IMAGE_AUTO_QUERIED,
      {
        sourceImage: this.session.getCurrentlySelectedImageFromFile().map(img => img.source)
      },
      {
        success: res.length > 0
      }
    );

    this.querySuggestions = [];
    res.forEach((sug) => {
      this.querySuggestions.push(sug.className.split(',')[0]);
    });

    if (this.querySuggestions.length >= 1 && submitQuery) {
      this.searchFormGroup.get('query')?.setValue(this.querySuggestions[0]);
      this.currentPage = 1;
      this.submitQuery();
    }

    console.log(res);
    this.session.doneAsyncAction();
  }

  /**
   * Just redirects to the main screen
   */
  public goToMain(): void {
    this._router.navigate(['']);
  }

  public showSelectedImageFromFile(): void {
    this._dialogSelectedImageFromFile.open(DialogShowSelectedImageFromFileComponent);
  }
}